import { keepPreviousData, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { backendService } from "../services/backend.service";
import type { BatchCostPredictionRequest, CostPredictionRequest, CostPredictionResponse } from "../utils/models";

export const usePredictCost = () => {
	return useMutation<CostPredictionResponse, Error, CostPredictionRequest>({
		mutationFn: async (params) => {
			const { data } = await backendService.predictCost(params);
			return data;
		},
	});
};

type BatchParams = Omit<BatchCostPredictionRequest, "offset" | "limit"> | null;

/**
 * Batch cost predictions via `useInfiniteQuery` (default 20/page + IntersectionObserver-driven
 * `fetchNextPage`). Callers that want everything upfront (e.g. AddNurse rendering into a virtualized
 * grid) can pass `pageSize = params.nurseIds.length` so the first page covers all rows in ONE request
 * — `getNextPageParam` then returns `undefined` and no further pages are fetched. The backend has no
 * limit cap. `pageSize` is part of the query key so different page sizes never share a cache entry.
 */
export const usePredictCostBatch = (params: BatchParams, pageSize = 20) => {
	const query = useInfiniteQuery({
		queryKey: [
			"predict-cost-batch",
			params?.departmentId,
			params?.nurseIds
				?.slice()
				.sort((a, b) => a - b)
				.join(","),
			params?.startTime,
			params?.endTime,
			JSON.stringify(params?.sort ?? []),
			params?.jobId,
			pageSize,
		],
		queryFn: async ({ pageParam = 0 }) => {
			if (!params) throw new Error("Missing params");
			const { data } = await backendService.predictCostBatch({
				...params,
				offset: pageParam as number,
				limit: pageSize,
			});
			return data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, _allPages, lastPageParam) => {
			const nextOffset = (lastPageParam as number) + pageSize;
			return nextOffset < lastPage.total ? nextOffset : undefined;
		},
		// Keep the previous query's data visible while a new query key (e.g. changed filters/times) refetches,
		// so callers can gate their loading skeleton on `isLoading` (initial only) and never blank on refetch.
		placeholderData: keepPreviousData,
		enabled: !!params && params.nurseIds.length > 0,
	});

	// Merge all pages into a single predictions map
	const predictions = useMemo(() => {
		if (!query.data?.pages) return {};
		const merged: Record<number, CostPredictionResponse> = {};
		for (const page of query.data.pages) {
			Object.assign(merged, page.predictions);
		}
		return merged;
	}, [query.data?.pages]);

	// Sorted nurse IDs from backend (same across all pages)
	const sortedNurseIds = useMemo(() => {
		return query.data?.pages[0]?.sortedNurseIds ?? [];
	}, [query.data?.pages]);

	return {
		predictions,
		sortedNurseIds,
		isLoading: query.isLoading,
		// The batch failed: a caller ranking by it must say so and fall back, rather than show an unranked list as ranked.
		isError: query.isError,
		// What failed, for a caller that reports it (`PREDICT_COST_FAILED_EVENT`); null while nothing has.
		error: query.error,
		isFetching: query.isFetching,
		// The shown predictions are the PREVIOUS key's (keepPreviousData) while this key's first fetch is in flight.
		isPlaceholderData: query.isPlaceholderData,
		isFetchingNextPage: query.isFetchingNextPage,
		hasNextPage: query.hasNextPage,
		fetchNextPage: query.fetchNextPage,
		total: query.data?.pages[0]?.total ?? 0,
	};
};
